import React, { useState, useEffect, useRef } from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import ScrollableTableContainer from '../common/ScrollableTableContainer';
import { formatDate as standardFormatDate } from '../../utils/dateUtils';

const LeaveReportView = ({ sheetData, refreshKey }) => {
  const { hasPermission } = useAuth();
  const [earnedLeaveData, setEarnedLeaveData] = useState(null);
  const [loadingEarnedLeave, setLoadingEarnedLeave] = useState(false);
  const [pgNumbers, setPgNumbers] = useState({}); // State for PG NO. values

  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const theadRef = useRef(null);

  // Dynamically calculate and update sticky top offsets for the 4 frozen header rows
  useEffect(() => {
    const updateHeaderPositions = () => {
      if (!theadRef.current) return;
      const rows = theadRef.current.querySelectorAll('tr');
      if (rows.length >= 4) {
        const baseOffset = rows[0].offsetTop;
        const r1Top = 0;
        let r2Top = rows[1].offsetTop - baseOffset;
        let r3Top = rows[2].offsetTop - baseOffset;
        let r4Top = rows[3].offsetTop - baseOffset;

        // Fallback to bounding rect if offsetTop has not resolved
        if (r2Top <= 0 && rows[0].getBoundingClientRect) {
          const r0Rect = rows[0].getBoundingClientRect();
          r2Top = Math.round(rows[1].getBoundingClientRect().top - r0Rect.top);
          r3Top = Math.round(rows[2].getBoundingClientRect().top - r0Rect.top);
          r4Top = Math.round(rows[3].getBoundingClientRect().top - r0Rect.top);
        }

        theadRef.current.style.setProperty('--freeze-r1-top', `${r1Top}px`);
        theadRef.current.style.setProperty('--freeze-r2-top', `${r2Top}px`);
        theadRef.current.style.setProperty('--freeze-r3-top', `${r3Top}px`);
        theadRef.current.style.setProperty('--freeze-r4-top', `${r4Top}px`);
      }
    };

    updateHeaderPositions();
    const timer = setTimeout(updateHeaderPositions, 150);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && theadRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderPositions();
      });
      resizeObserver.observe(theadRef.current);
    }
    window.addEventListener('resize', updateHeaderPositions);

    return () => {
      clearTimeout(timer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeaderPositions);
    };
  }, [earnedLeaveData]);

  const handleDownloadExcel = async () => {
    if (!sheetData?.employee?.id || downloadingExcel) return;
    const year = sheetData?.year || new Date().getFullYear();
    setDownloadingExcel(true);
    try {
      const response = await axios.get(
        `/v1/leave-sheet/employee/${sheetData.employee.id}/excel?year=${year}`,
        { responseType: 'blob' }
      );

      // Extract filename from header if available or fallback
      let fileName = `Leave_Sheet_${sheetData?.employee?.full_name || 'Employee'}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading leave sheet Excel:', error);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const formatDate = (dateStr) => standardFormatDate(dateStr, '');

  // Handle PG NO. value change
  const handlePgNumberChange = (rowIndex, pgField, value) => {
    const rowKey = earnedLeaveData?.earned_leave?.rows[rowIndex]?.duty_from;
    if (!rowKey) return;

    setPgNumbers(prev => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        [pgField]: value
      }
    }));

    // Debounced save to backend
    savePgNumber(rowIndex, pgField, value);
  };

  // Save PG NO. to backend
  const savePgNumber = async (rowIndex, pgField, value) => {
    const row = earnedLeaveData?.earned_leave?.rows[rowIndex];
    if (!row || !sheetData?.employee?.id) return;

    try {
      const year = sheetData?.year || new Date().getFullYear();
      const payload = {
        employee_id: sheetData.employee.id,
        year: year,
        period_from: row.duty_from,
        period_to: row.duty_to,
        [pgField]: value || null
      };

      await axios.put('/v1/leave-sheet/page-number', payload);
    } catch (error) {
      console.error('Error saving page number:', error);
    }
  };

  // Fetch earned leave data when sheetData changes or refreshKey changes
  const fetchEarnedLeaveData = async () => {
    if (!sheetData?.employee?.id) return;

    setLoadingEarnedLeave(true);
    try {
      const response = await axios.get(`/v1/leave-sheet/employee/${sheetData.employee.id}/earned-leave`);
      setEarnedLeaveData(response.data);

      // Initialize PG numbers from fetched data
      const pgMap = {};
      response.data.earned_leave.rows.forEach((row, idx) => {
        if (row.duty_from) {
          pgMap[row.duty_from] = {
            pg_no_1: row.pg_no_1 || '',
            pg_no_2: row.pg_no_2 || '',
            pg_no_3: row.pg_no_3 || '',
            pg_no_4: row.pg_no_4 || '',
          };
        }
      });
      setPgNumbers(pgMap);
    } catch (error) {
      console.error('Error fetching earned leave data:', error);
    } finally {
      setLoadingEarnedLeave(false);
    }
  };

  useEffect(() => {
    fetchEarnedLeaveData();
  }, [sheetData?.employee?.id, sheetData?.year, refreshKey]);

  return (
    <div className="flex flex-col h-full bg-white relative">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            size: landscape;
            margin: 5mm;
          }
          .leave-sticky-intersection,
          .leave-sticky-row1,
          .leave-sticky-row2,
          .leave-sticky-row3,
          .leave-sticky-row4,
          .leave-sticky-col {
            position: static !important;
          }
        }
        .vertical-text {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          text-align: center;
          padding: 8px 4px !important;
          max-height: 250px;
          white-space: nowrap;
        }
        .leave-table {
          border-collapse: separate !important;
          border-spacing: 0 !important;
          border-top: 1px solid #374151 !important;
          border-left: 1px solid #374151 !important;
          empty-cells: show !important;
        }
        .leave-table th, .leave-table td {
          border-right: 1px solid #374151 !important;
          border-bottom: 1px solid #374151 !important;
          border-top: none !important;
          border-left: none !important;
          padding: 4px;
          text-align: center;
          font-size: 11px;
          box-sizing: border-box;
        }
        .leave-table th {
          font-weight: 600;
          background-color: #f3f4f6;
        }

        /* Column widths & positions */
        .leave-col-1 {
          left: 0px !important;
          width: 85px !important;
          min-width: 85px !important;
          max-width: 85px !important;
        }
        .leave-col-2 {
          left: 85px !important;
          width: 85px !important;
          min-width: 85px !important;
          max-width: 85px !important;
        }
        .leave-col-3 {
          left: 170px !important;
          width: 20px !important;
          min-width: 20px !important;
          max-width: 20px !important;
        }
        .leave-col-4 {
          left: 190px !important;
          width: 50px !important;
          min-width: 50px !important;
          max-width: 50px !important;
          border-right: 2px solid #374151 !important;
        }
        .leave-col-1-3 {
          left: 0px !important;
          width: 190px !important;
          min-width: 190px !important;
          max-width: 190px !important;
        }
        .leave-col-1-4 {
          left: 0px !important;
          width: 240px !important;
          min-width: 240px !important;
          max-width: 240px !important;
          border-right: 2px solid #374151 !important;
        }

        /* Sticky Row Top offsets */
        .leave-sticky-row1 {
          position: sticky !important;
          top: var(--freeze-r1-top, 0px) !important;
          z-index: 25 !important;
          background-color: #f3f4f6 !important;
        }
        .leave-sticky-row2 {
          position: sticky !important;
          top: var(--freeze-r2-top, 28px) !important;
          z-index: 25 !important;
          background-color: #f3f4f6 !important;
        }
        .leave-sticky-row3 {
          position: sticky !important;
          top: var(--freeze-r3-top, 56px) !important;
          z-index: 25 !important;
          background-color: #f3f4f6 !important;
        }
        .leave-sticky-row4 {
          position: sticky !important;
          top: var(--freeze-r4-top, 84px) !important;
          z-index: 25 !important;
          background-color: #f3f4f6 !important;
        }

        /* Sticky Intersection (Rows 1-4, Columns 1-4) */
        .leave-sticky-intersection {
          position: sticky !important;
          z-index: 40 !important;
          background-color: #f3f4f6 !important;
        }
        tr.leave-row-1 .leave-sticky-intersection {
          top: var(--freeze-r1-top, 0px) !important;
        }
        tr.leave-row-2 .leave-sticky-intersection {
          top: var(--freeze-r2-top, 28px) !important;
        }
        tr.leave-row-3 .leave-sticky-intersection {
          top: var(--freeze-r3-top, 56px) !important;
        }

        /* Sticky Left Columns in Body and Row 5 */
        .leave-sticky-col {
          position: sticky !important;
          z-index: 15 !important;
          background-color: #ffffff !important;
        }
        .leave-sticky-col.leave-row5 {
          z-index: 20 !important;
          background-color: #e5e7eb !important;
        }
        .leave-data-row:hover .leave-sticky-col {
          background-color: #f9fafb !important;
        }

        /* Crisp divider separating Top 4 Rows from Scrolling data */
        .leave-header-bottom-border {
          border-bottom: 2px solid #374151 !important;
        }
      `}</style>

      <div className="flex justify-end p-4 border-b">
        {hasPermission('leave:sheet:export') && (
          <button
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm disabled:opacity-50 cursor-pointer"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloadingExcel ? 'Downloading...' : 'Download Excel'}
          </button>
        )}
      </div>

      <div id="print-area" className="flex-1 overflow-auto p-4 w-full">
        {/* Employee Info Section */}
        <div className="mb-4">
          <table className="border-collapse border border-gray-700 text-xs text-center font-medium">
            <tbody>
              <tr>
                <td className="border border-gray-700 p-2 bg-gray-100">NAME OF GOVERNMENT SERVANT</td>
                <td className="border border-gray-700 p-2 bg-gray-100">DATE OF BIRTH</td>
                <td className="border border-gray-700 p-2 bg-gray-100">COMMENCEMENT</td>
                <td className="border border-gray-700 p-2 bg-gray-100">DATE OF RETIRED</td>
              </tr>
              <tr>
                <td className="border border-gray-700 p-2 uppercase">
                  {earnedLeaveData?.employee?.name || sheetData?.employee?.full_name || sheetData?.employee?.first_name + " " + sheetData?.employee?.last_name || "SMT. MANGAL RAVI KHANDAGALE"}
                  <br />({sheetData?.employee?.designation || "MATRON"})
                </td>
                <td className="border border-gray-700 p-2">
                  {earnedLeaveData?.employee?.date_of_birth ? formatDate(earnedLeaveData.employee.date_of_birth) :
                    (sheetData?.employee?.date_of_birth ? formatDate(sheetData.employee.date_of_birth) : "02.06.1966")}
                </td>
                <td className="border border-gray-700 p-2">
                  {earnedLeaveData?.employee?.date_of_joining ? formatDate(earnedLeaveData.employee.date_of_joining) :
                    (sheetData?.employee?.join_date ? formatDate(sheetData.employee.join_date) : "16.04.1990")}
                </td>
                <td className="border border-gray-700 p-2">
                  {earnedLeaveData?.employee?.date_of_retirement ? formatDate(earnedLeaveData.employee.date_of_retirement) :
                    (sheetData?.employee?.retirement_date ? formatDate(sheetData.employee.retirement_date) : "30.06.2026")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ScrollableTableContainer
          header={null}
          maxHeight={600}
          showTopScrollbar={true}
          stickyHeader={false}
        >
          <table className="leave-table min-w-max border border-gray-700 w-full">
            <colgroup>
              <col style={{ width: '85px', minWidth: '85px', maxWidth: '85px' }} />
              <col style={{ width: '85px', minWidth: '85px', maxWidth: '85px' }} />
              <col style={{ width: '20px', minWidth: '20px', maxWidth: '20px' }} />
              <col style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }} />
            </colgroup>
            <thead ref={theadRef}>
              {/* Row 1 */}
              <tr className="leave-row-1">
                <th colSpan="4" className="leave-sticky-intersection leave-col-1-4">
                  EARNED LEAVE
                </th>
                <th colSpan="5" className="leave-sticky-row1">
                  EARNED LEAVE
                </th>
                <th className="leave-sticky-row1"></th>
                <th colSpan="5" className="leave-sticky-row1">HPL from private affairs and<br />on Medical Certificate</th>
                <th colSpan="3" className="leave-sticky-row1">Leave taken<br />Against<br />the<br />earning<br />of half<br />pay</th>
                <th className="leave-sticky-row1"></th>
                <th colSpan="16" className="leave-sticky-row1">Including commuted leave and leave not due</th>
              </tr>

              {/* Row 2 */}
              <tr className="leave-row-2">
                <th colSpan="3" className="leave-sticky-intersection leave-col-1-3">DUTY</th>
                <th rowSpan="3" className="vertical-text leave-sticky-intersection leave-col-4 leave-header-bottom-border">LEAVE EARNED</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">LEAVE AT CR 9+4 SUBJECT TO<br />APRO. LIMIT</th>
                <th colSpan="3" className="leave-sticky-row2">LEAVE TAKEN</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">BALANCE ON RETURN FROM<br />LAVE</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">PG NO.</th>

                <th colSpan="3" className="leave-sticky-row2">Length of Service</th>
                <th colSpan="2" className="leave-sticky-row2">Credit of<br />leave</th>

                <th rowSpan="3" className="leave-sticky-row2 leave-header-bottom-border">From</th>
                <th rowSpan="3" className="leave-sticky-row2 leave-header-bottom-border">To</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">No. of days</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">PG NO.</th>

                <th colSpan="4" className="leave-sticky-row2">Commuted leave on<br />medical certificte on full<br />pay</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">PG NO.</th>
                <th colSpan="6" className="leave-sticky-row2">Leave not due limited to 360 days in entire service</th>

                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">EOL</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">Total of leave not due (Cols.<br />24+27) PG. NO</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">Total half pay leave taken (Cols.<br />17+21+28)</th>
                <th rowSpan="3" className="vertical-text leave-sticky-row2 leave-header-bottom-border">Balance of return from leave (Cols 14<br />-<br />29)</th>
              </tr>

              {/* Row 3 */}
              <tr className="leave-row-3">
                <th rowSpan="2" className="leave-sticky-intersection leave-col-1 leave-header-bottom-border">FROM</th>
                <th rowSpan="2" className="leave-sticky-intersection leave-col-2 leave-header-bottom-border">TO</th>
                <th rowSpan="2" className="leave-sticky-intersection leave-col-3 leave-header-bottom-border">&nbsp;</th>

                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">FROM</th>
                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">TO</th>
                <th rowSpan="2" className="vertical-text leave-sticky-row3 leave-header-bottom-border">NO. OF DAYS</th>

                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">From</th>
                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">to</th>
                <th rowSpan="2" className="w-8 leave-sticky-row3 leave-header-bottom-border"></th>

                <th rowSpan="2" className="vertical-text leave-sticky-row3 leave-header-bottom-border">leave earned<br />in days</th>
                <th rowSpan="2" className="vertical-text leave-sticky-row3 leave-header-bottom-border">leave at credit<br />30+13</th>

                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">From</th>
                <th rowSpan="2" className="leave-sticky-row3 leave-header-bottom-border">To</th>
                <th rowSpan="2" className="vertical-text leave-sticky-row3 leave-header-bottom-border">No of days</th>
                <th rowSpan="2" className="vertical-text leave-sticky-row3 leave-header-bottom-border">Commuted leave converted<br />into half pay leave twice of Col.</th>

                <th colSpan="3" className="leave-sticky-row3">On Medical Certificate</th>
                <th colSpan="3" className="leave-sticky-row3">Certificate limited to 180</th>
              </tr>

              {/* Row 4 */}
              <tr className="leave-row-4">
                <th className="leave-sticky-row4 leave-header-bottom-border">From</th>
                <th className="leave-sticky-row4 leave-header-bottom-border">To</th>
                <th className="vertical-text leave-sticky-row4 leave-header-bottom-border">No. of days</th>

                <th className="leave-sticky-row4 leave-header-bottom-border">From</th>
                <th className="leave-sticky-row4 leave-header-bottom-border">To</th>
                <th className="vertical-text leave-sticky-row4 leave-header-bottom-border">No. of days</th>
              </tr>

              {/* Row 5 - Numbers */}
              <tr className="bg-gray-200 leave-row-5">
                <th className="leave-sticky-col leave-col-1 leave-row5">1</th>
                <th className="leave-sticky-col leave-col-2 leave-row5">2</th>
                <th className="leave-sticky-col leave-col-3 leave-row5">&nbsp;</th>
                <th className="leave-sticky-col leave-col-4 leave-row5">4</th>
                <th>5</th>
                <th>6</th>
                <th>7</th>
                <th>8</th>
                <th>9</th>
                <th></th>
                <th>10</th>
                <th>11</th>
                <th></th>
                <th>13</th>
                <th>14</th>
                <th>15</th>
                <th>16</th>
                <th>17</th>
                <th></th>
                <th>18</th>
                <th>19</th>
                <th>20</th>
                <th>21</th>
                <th></th>
                <th>22</th>
                <th>23</th>
                <th>24</th>
                <th>25</th>
                <th>26</th>
                <th>27</th>
                <th>28</th>
                <th>29</th>
                <th>30</th>
                <th>31</th>
              </tr>
            </thead>
            <tbody>
              {loadingEarnedLeave ? (
                <tr>
                  <td colSpan="35" className="p-4 text-center text-gray-500">
                    Loading Earned Leave data...
                  </td>
                </tr>
              ) : earnedLeaveData?.earned_leave?.rows?.length > 0 ? (
                earnedLeaveData.earned_leave.rows.map((row, index) => (
                  <tr key={index} className="leave-data-row hover:bg-gray-50">
                    {/* Column 1: Duty From */}
                    <td className="leave-sticky-col leave-col-1">{formatDate(row.duty_from)}</td>
                    {/* Column 2: Duty To */}
                    <td className="leave-sticky-col leave-col-2">{formatDate(row.duty_to)}</td>
                    {/* Column 3: Empty */}
                    <td className="leave-sticky-col leave-col-3">&nbsp;</td>
                    {/* Column 4: Leave Earned */}
                    <td className="leave-sticky-col leave-col-4">{row.leave_earned !== null ? row.leave_earned : ''}</td>
                    {/* Column 5: Leave at Credit */}
                    <td>{row.leave_at_credit !== null ? row.leave_at_credit : ''}</td>
                    {/* Column 6: Leave Taken From */}
                    <td>{formatDate(row.leave_taken_from)}</td>
                    {/* Column 7: Leave Taken To */}
                    <td>{formatDate(row.leave_taken_to)}</td>
                    {/* Column 8: No. of Days */}
                    <td>{row.no_of_days !== null ? row.no_of_days : ''}</td>
                    {/* Column 9: Balance on Return */}
                    <td>{row.balance_on_return !== null ? row.balance_on_return : ''}</td>
                    {/* PG NO. 1 (after Column 9) */}
                    <td>
                      <input
                        type="text"
                        value={pgNumbers[row.duty_from]?.pg_no_1 || ''}
                        onChange={(e) => handlePgNumberChange(index, 'pg_no_1', e.target.value)}
                        className="w-12 border border-gray-300 px-1 py-1 text-xs text-center focus:outline-none focus:border-blue-500"
                        placeholder="PG"
                        maxLength={3}
                      />
                    </td>
                    {/* Column 10: HPL Length of Service From */}
                    <td>{formatDate(row.hpl_from)}</td>
                    {/* Column 11: HPL Length of Service To */}
                    <td>{formatDate(row.hpl_to)}</td>
                    {/* Column 12: Empty (not implemented yet) */}
                    <td></td>
                    {/* Column 13: HPL Leave Earned */}
                    <td>{row.hpl_leave_earned !== null ? row.hpl_leave_earned : ''}</td>
                    {/* Column 14: HPL Leave at Credit */}
                    <td>{row.hpl_leave_at_credit !== null ? row.hpl_leave_at_credit : ''}</td>
                    {/* Column 15: HPL Leave Taken From */}
                    <td>{formatDate(row.hpl_taken_from)}</td>
                    {/* Column 16: HPL Leave Taken To */}
                    <td>{formatDate(row.hpl_taken_to)}</td>
                    {/* Column 17: HPL Leave Taken Days */}
                    <td>{row.hpl_taken_days !== null ? row.hpl_taken_days : ''}</td>
                    {/* PG NO. 2 (after Column 17) */}
                    <td>
                      <input
                        type="text"
                        value={pgNumbers[row.duty_from]?.pg_no_2 || ''}
                        onChange={(e) => handlePgNumberChange(index, 'pg_no_2', e.target.value)}
                        className="w-12 border border-gray-300 px-1 py-1 text-xs text-center focus:outline-none focus:border-blue-500"
                        placeholder="PG"
                        maxLength={3}
                      />
                    </td>
                    {/* Column 18: FPL From */}
                    <td>{formatDate(row.fpl_from)}</td>
                    {/* Column 19: FPL To */}
                    <td>{formatDate(row.fpl_to)}</td>
                    {/* Column 20: FPL Days */}
                    <td>{row.fpl_days !== null ? row.fpl_days : ''}</td>
                    {/* Column 21: Commuted Leave Converted into Half Pay Leave */}
                    <td>{row.commuted_leave_converted !== null ? row.commuted_leave_converted : ''}</td>
                    {/* PG NO. 3 (after Column 21) */}
                    <td>
                      <input
                        type="text"
                        value={pgNumbers[row.duty_from]?.pg_no_3 || ''}
                        onChange={(e) => handlePgNumberChange(index, 'pg_no_3', e.target.value)}
                        className="w-12 border border-gray-300 px-1 py-1 text-xs text-center focus:outline-none focus:border-blue-500"
                        placeholder="PG"
                        maxLength={3}
                      />
                    </td>
                    {/* Columns 22-24: Empty for now */}
                    <td></td><td></td><td></td>
                    {/* Column 25: LOP/EOL From */}
                    <td>{formatDate(row.lop_eol_from)}</td>
                    {/* Column 26: LOP/EOL To */}
                    <td>{formatDate(row.lop_eol_to)}</td>
                    {/* Column 27: LOP/EOL Days */}
                    <td>{row.lop_eol_days !== null ? row.lop_eol_days : ''}</td>
                    {/* Column 28: LOP/EOL Type */}
                    <td>{row.lop_eol_type !== null ? row.lop_eol_type : ''}</td>
                    {/* Column 29: Empty for now */}
                    {/* <td></td> */}
                    {/* PG NO. 4 (after Column 29) */}
                    <td>
                      <input
                        type="text"
                        value={pgNumbers[row.duty_from]?.pg_no_4 || ''}
                        onChange={(e) => handlePgNumberChange(index, 'pg_no_4', e.target.value)}
                        className="w-full border border-gray-300 px-1 py-1 text-xs text-center focus:outline-none focus:border-blue-500"
                        placeholder="PG"
                      />
                    </td>
                    {/* Column 30: Empty */}
                    <td></td>
                    {/* Column 31: Balance of Return from Leave */}
                    <td>{row.balance_of_return !== null ? row.balance_of_return : ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="35" className="p-4 text-center text-gray-500">
                    No Earned Leave data available
                  </td>
                </tr>
              )}
              {/* Adding some empty rows for visual structure just like the paper */}
              {[...Array(5)].map((_, index) => (
                <tr key={`empty-${index}`} className="h-6 leave-data-row hover:bg-gray-50">
                  <td className="leave-sticky-col leave-col-1"></td>
                  <td className="leave-sticky-col leave-col-2"></td>
                  <td className="leave-sticky-col leave-col-3">&nbsp;</td>
                  <td className="leave-sticky-col leave-col-4"></td>
                  {[...Array(31)].map((_, i) => <td key={i}></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTableContainer>
      </div>
    </div>
  );
};

export default LeaveReportView;
